import { Box, Group, Stack, Text } from "@mantine/core";

export interface Stage {
  key: string;
  label: string;
  /** When it happened, already formatted. Absent for a stage not yet reached. */
  at?: string;
  /** Who did it. The design shows this and it is worth showing. */
  by?: string;
}

/**
 * Where a service job has got to.
 *
 * BUILT FOR OUR LIFECYCLE, not the design's. The PDF shows Received →
 * Diagnosed → Under Repair → Ready → Delivered; this app's stages are
 * Collected → Received → Diagnosed → Outcome → Delivered, and there is no
 * "Under Repair" or "Ready" behind it. Drawing five stages we do not have
 * would be a stepper that lies about where the battery is.
 *
 * Completed = teal check, current = filled indigo dot, future = hollow gray.
 * Each carries its timestamp and actor, because "who moved it and when" is
 * the question this component is really answering.
 */
export function StageStepper({
  stages,
  currentIndex,
  orientation = "horizontal",
}: {
  stages: Stage[];
  /** Index of the stage the job is IN. Everything before it is done. */
  currentIndex: number;
  orientation?: "horizontal" | "vertical";
}) {
  if (orientation === "vertical") {
    return (
      <Stack gap="md">
        {stages.map((stage, index) => (
          <Group key={stage.key} gap="sm" align="flex-start" wrap="nowrap">
            <StageDot index={index} currentIndex={currentIndex} />
            <Stack gap={0}>
              <Text fz="md" fw={index === currentIndex ? 600 : 400} c="gray.9">
                {stage.label}
              </Text>
              {(stage.at || stage.by) && (
                <Text fz="sm" c="gray.6">
                  {[stage.at, stage.by].filter(Boolean).join(" · ")}
                </Text>
              )}
            </Stack>
          </Group>
        ))}
      </Stack>
    );
  }

  return (
    <Group gap={0} align="flex-start" wrap="nowrap" style={{ width: "100%" }}>
      {stages.map((stage, index) => (
        <Stack
          key={stage.key}
          gap={4}
          align="center"
          style={{ flex: 1, minWidth: 0 }}
        >
          <Group gap={0} wrap="nowrap" style={{ width: "100%" }} justify="center">
            {/* Connectors are drawn as siblings rather than borders so the
                first and last stages do not sprout a half-rule into space. */}
            <Box
              aria-hidden
              style={{
                height: 2,
                flex: 1,
                backgroundColor:
                  index === 0
                    ? "transparent"
                    : index <= currentIndex
                      ? "var(--mantine-color-teal-6)"
                      : "var(--mantine-color-gray-3)",
              }}
            />
            <StageDot index={index} currentIndex={currentIndex} />
            <Box
              aria-hidden
              style={{
                height: 2,
                flex: 1,
                backgroundColor:
                  index === stages.length - 1
                    ? "transparent"
                    : index < currentIndex
                      ? "var(--mantine-color-teal-6)"
                      : "var(--mantine-color-gray-3)",
              }}
            />
          </Group>
          <Text
            fz="sm"
            fw={index === currentIndex ? 600 : 400}
            c={index <= currentIndex ? "gray.9" : "gray.5"}
            ta="center"
            lh={1.3}
          >
            {stage.label}
          </Text>
          {(stage.at || stage.by) && (
            <Text fz="xs" c="gray.6" ta="center" lh={1.3}>
              {[stage.at, stage.by].filter(Boolean).join(" · ")}
            </Text>
          )}
        </Stack>
      ))}
    </Group>
  );
}

function StageDot({ index, currentIndex }: { index: number; currentIndex: number }) {
  const done = index < currentIndex;
  const current = index === currentIndex;
  return (
    <Box
      aria-hidden
      w={18}
      h={18}
      style={{
        flexShrink: 0,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        lineHeight: 1,
        color: "var(--mantine-color-white)",
        backgroundColor: done
          ? "var(--mantine-color-teal-6)"
          : current
            ? "var(--mantine-color-indigo-7)"
            : "transparent",
        border: done || current ? "none" : "2px solid var(--mantine-color-gray-3)",
      }}
    >
      {done ? "✓" : ""}
    </Box>
  );
}
